/**
 * Word Highlighting Utilities for TTS Audio Playback
 * 
 * Handles matching TTS word timings to DOM word elements and highlighting during playback
 */

// Flag to enable/disable word highlighting
export const ENABLE_WORD_HIGHLIGHTING = false;

// Remove punctuation from text for matching
function removePunctuation(text: string): string {
  return text.replace(/[.,!?;:'"()\[\]{}\-—–…]/g, '');
}

// Check if text is only punctuation
function isOnlyPunctuation(text: string): boolean {
  return /^[.,!?;:'"()\[\]{}\-—–…]+$/.test(text);
}

// Normalize text for matching (lowercase, trim)
function normalizeText(text: string): string {
  return text.toLowerCase().trim();
}

/**
 * Match a TTS word to a DOM word element
 * Returns the word element if found, null otherwise
 */
function matchWord(
  ttsWord: string,
  wordElements: HTMLElement[],
  startIndex: number
): { element: HTMLElement; index: number } | null {
  const normalizedTtsWord = normalizeText(ttsWord);
  const ttsWordNoPunct = removePunctuation(normalizedTtsWord);
  const isTtsPunctuationOnly = isOnlyPunctuation(normalizedTtsWord);

  // Search through word elements starting from startIndex
  for (let i = startIndex; i < wordElements.length; i++) {
    const wordEl = wordElements[i];
    const wordText = wordEl.getAttribute('data-word-text') || wordEl.textContent || '';
    const normalizedWordText = normalizeText(wordText);
    const wordTextNoPunct = removePunctuation(normalizedWordText);

    // a. Try exact match
    if (normalizedWordText === normalizedTtsWord) {
      return { element: wordEl, index: i };
    }

    // b. Try match with punctuation removed from both
    if (ttsWordNoPunct && wordTextNoPunct === ttsWordNoPunct) {
      return { element: wordEl, index: i };
    }

    // c. If TTS word is just punctuation, match that punctuation
    if (isTtsPunctuationOnly) {
      // Check if the word element contains this punctuation (could be standalone or part of word)
      if (normalizedWordText.includes(normalizedTtsWord)) {
        return { element: wordEl, index: i };
      }
      // Also check if punctuation is at the end of previous word (e.g., "this," split into "this" and ",")
      if (i > startIndex && i > 0) {
        const prevWordEl = wordElements[i - 1];
        const prevWordText = prevWordEl.getAttribute('data-word-text') || prevWordEl.textContent || '';
        const normalizedPrevWordText = normalizeText(prevWordText);
        if (normalizedPrevWordText.endsWith(normalizedTtsWord)) {
          // This punctuation belongs to the previous word - match to previous word
          return { element: prevWordEl, index: i - 1 };
        }
      }
    }
  }

  return null;
}

/**
 * Mark blocks and words that are included in TTS
 * Adds data-tts-included="true" attribute to blocks and words
 */
export function markTtsIncludedElements(
  pageIndices: number[],
  blocksByPage: Map<number, any[]>
): void {
  // First, clear all previous marks
  document.querySelectorAll('[data-tts-included="true"]').forEach(el => {
    el.removeAttribute('data-tts-included');
  });

  // Mark blocks and words for each page
  pageIndices.forEach(pageIndex => {
    const blocks = blocksByPage.get(pageIndex) || [];
    const readBlocks = blocks
      .filter(block => block.readBlock === true)
      .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));

    // Find the page overlay container
    const pageContainer = document.querySelector(
      `.word-overlay-container[data-page-overlay="page-${pageIndex}"]`
    ) as HTMLElement;

    if (!pageContainer) {
      console.warn(`[WordHighlighting] Page container not found for page ${pageIndex}`);
      return;
    }

    readBlocks.forEach(block => {
      // Find block element within the page container
      const blockEl = pageContainer.querySelector(
        `.overlay-block[data-block-id="${block.blockId}"]`
      ) as HTMLElement;

      if (blockEl) {
        blockEl.setAttribute('data-tts-included', 'true');

        // Mark all words in this block
        if (block.words) {
          block.words.forEach((word: any) => {
            const wordEl = blockEl.querySelector(
              `.overlay-word[data-word-id="${word.wordId}"]`
            ) as HTMLElement;

            if (wordEl) {
              wordEl.setAttribute('data-tts-included', 'true');
            }
          });
        }
      }
    });
  });
}

/**
 * Get all marked word elements in read order across pages
 */
function getMarkedWordElementsInOrder(pageIndices: number[]): HTMLElement[] {
  const wordElements: HTMLElement[] = [];

  pageIndices.forEach(pageIndex => {
    const pageContainer = document.querySelector(
      `.word-overlay-container[data-page-overlay="page-${pageIndex}"]`
    ) as HTMLElement;

    if (!pageContainer) return;

    // Get all marked blocks in this page, sorted by read order
    const markedBlocks = Array.from(
      pageContainer.querySelectorAll('.overlay-block[data-tts-included="true"]')
    ) as HTMLElement[];

    // Sort blocks by blockReadOrder (stored in data attributes or need to get from context)
    // For now, we'll rely on DOM order which should match read order
    markedBlocks.forEach(blockEl => {
      const blockId = blockEl.getAttribute('data-block-id');
      if (!blockId) return;

      // Get all marked words in this block
      const wordsInBlock = Array.from(
        blockEl.querySelectorAll('.overlay-word[data-tts-included="true"]')
      ) as HTMLElement[];

      // Words should already be in order within the block
      wordElements.push(...wordsInBlock);
    });
  });

  return wordElements;
}

/**
 * Process word timings from TTS response and wrap matched words in spans
 * Returns the number of words successfully matched
 */
export function processWordTimings(
  wordTimings: Array<{ word: string; offset: number }>,
  pageIndices: number[]
): number {
  // Cleanup: Restore original IDs if they exist
  document.querySelectorAll('[id^="word-for-audio-highlight-"]').forEach(el => {
    const originalId = el.getAttribute('data-original-id');
    if (originalId) {
      el.id = originalId;
      el.removeAttribute('data-original-id');
    } else {
      el.removeAttribute('id');
    }
  });

  // Get all marked word elements in order (left page first, then right page)
  const wordElements = getMarkedWordElementsInOrder(pageIndices);

  if (wordElements.length === 0) {
    console.warn('[WordHighlighting] No marked word elements found');
    return 0;
  }

  let searchIndex = 0;
  let matchedCount = 0;

  // Loop through each word from TTS
  for (let i = 0; i < wordTimings.length; i++) {
    const timing = wordTimings[i];
    const match = matchWord(timing.word, wordElements, searchIndex);

    if (match) {
      const { element, index } = match;

      // Add ID to the word element for highlighting
      // Store original ID if it exists
      if (!element.hasAttribute('data-original-id')) {
        const originalId = element.id;
        if (originalId) {
          element.setAttribute('data-original-id', originalId);
        }
      }
      element.id = `word-for-audio-highlight-${i}`;

      // Update search index to after this match
      searchIndex = index + 1;
      matchedCount++;
    } else {
      // Word not found, skip it (don't update searchIndex)
      console.log(`[WordHighlighting] Word "${timing.word}" not found, skipping`);
    }
  }

  console.log(`[WordHighlighting] Matched ${matchedCount} of ${wordTimings.length} words`);
  return matchedCount;
}

/**
 * Start highlighting words during audio playback
 * Returns cleanup function
 */
export function startWordHighlighting(
  audio: HTMLAudioElement,
  wordTimings: Array<{ word: string; offset: number }>
): () => void {
  let currentIndex = 0;
  let highlightInterval: number | null = null;

  // Remove all highlights first
  document.querySelectorAll('.tts-word-highlighted').forEach(el => {
    el.classList.remove('tts-word-highlighted');
  });

  const updateHighlight = () => {
    if (!audio || audio.paused || audio.ended) {
      return;
    }

    const currentTime = audio.currentTime * 1000; // Convert to milliseconds

    // Find the current word based on timing
    while (currentIndex < wordTimings.length) {
      const timing = wordTimings[currentIndex];
      
      if (currentTime >= timing.offset) {
        // Remove highlight from previous word
        if (currentIndex > 0) {
          const prevSpan = document.getElementById(`word-for-audio-highlight-${currentIndex - 1}`);
          if (prevSpan) {
            prevSpan.classList.remove('tts-word-highlighted');
          }
        }

        // Add highlight to current word
        const currentSpan = document.getElementById(`word-for-audio-highlight-${currentIndex}`);
        if (currentSpan) {
          currentSpan.classList.add('tts-word-highlighted');
        }

        currentIndex++;
      } else {
        break;
      }
    }
  };

  // Use timeupdate event for more accurate timing
  audio.addEventListener('timeupdate', updateHighlight);

  // Also use interval as fallback (every 100ms)
  highlightInterval = window.setInterval(updateHighlight, 100);

  // Cleanup function
  return () => {
    audio.removeEventListener('timeupdate', updateHighlight);
    if (highlightInterval !== null) {
      clearInterval(highlightInterval);
    }
    // Remove all highlights
    document.querySelectorAll('.tts-word-highlighted').forEach(el => {
      el.classList.remove('tts-word-highlighted');
    });
  };
}

/**
 * Cleanup word highlighting IDs and marks
 */
export function cleanupWordHighlighting(): void {
  // Restore original IDs and remove highlight IDs
  document.querySelectorAll('[id^="word-for-audio-highlight-"]').forEach(el => {
    const originalId = el.getAttribute('data-original-id');
    if (originalId) {
      el.id = originalId;
      el.removeAttribute('data-original-id');
    } else {
      el.removeAttribute('id');
    }
  });

  // Remove marks
  document.querySelectorAll('[data-tts-included="true"]').forEach(el => {
    el.removeAttribute('data-tts-included');
  });
}

